import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Mail, Bell, ChevronDown, ChevronUp } from 'lucide-react';
import { 
  fetchNotificationPreferences,
  updateNotificationSubscriptions
} from '../store/slices/notificationSettingsSlice';
import { toast } from 'react-toastify';
import useScreenSize from '../hooks/useScreenSize';

// Simplified notification types - email only
const NOTIFICATION_TYPES = [
  {
    id: 'load_assignment',
    name: 'Load Assignments',
    description: 'Get notified when a new load is awarded to your organization',
    // Maps to backend type
    backendTypes: ['load_assignment']
  },
  {
    id: 'new_bid',
    name: 'New Bids',
    description: 'Receive alerts when new bids are placed on your organization\'s loads',
    backendTypes: ['new_bid']
  },
  {
    id: 'billing_updates',
    name: 'Billing Updates',
    description: 'Receive notifications when payments are received, released, and invoices are uploaded',
    // Consolidated from multiple backend types
    backendTypes: ['payment_received', 'payment_released', 'invoice_uploaded']
  },
  {
    id: 'password_change',
    name: 'Security Alerts',
    description: 'Receive notifications when your password is changed or other security-related alerts',
    backendTypes: ['password_change']
  }
];

const NotificationSettings = ({ isEditing, setIsEditing }) => {
  const dispatch = useDispatch();
  const { isMobile } = useScreenSize();
  const {
    subscriptions,
    loading
  } = useSelector(state => state.notificationSettings);
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [localSettings, setLocalSettings] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Fetch preferences on mount
  useEffect(() => {
    dispatch(fetchNotificationPreferences());
  }, [dispatch]);

  // Initialize local settings from subscriptions
  useEffect(() => {
    if (Object.keys(subscriptions).length > 0) {
      const settings = {};
      NOTIFICATION_TYPES.forEach(type => {
        // Check if any of the backend types have email enabled
        const isEnabled = type.backendTypes.some(
          backendType => subscriptions[backendType]?.includes('email')
        );
        settings[type.id] = isEnabled;
      });
      setLocalSettings(settings);
    }
  }, [subscriptions]);

  // Count enabled notifications for summary
  const getEnabledCount = () => {
    return Object.values(localSettings).filter(Boolean).length;
  };

  const handleToggle = (typeId) => {
    if (!isEditing) return;
    setLocalSettings(prev => ({
      ...prev,
      [typeId]: !prev[typeId]
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    const toastId = toast.loading('Saving preferences...', {
      position: isMobile ? "bottom-center" : "top-right"
    });
    
    try {
      // Build subscriptions array for API
      const subscriptionsArray = [];
      
      NOTIFICATION_TYPES.forEach(type => {
        const enabled = localSettings[type.id];
        type.backendTypes.forEach(backendType => {
          subscriptionsArray.push({
            type: backendType,
            channel: 'email',
            subscribed: enabled
          });
        });
      });

      await dispatch(updateNotificationSubscriptions(subscriptionsArray)).unwrap();
      
      toast.update(toastId, {
        render: 'Email preferences updated',
        type: 'success',
        isLoading: false,
        autoClose: 2500
      });
      
      setIsEditing(false);
    } catch (error) {
      toast.update(toastId, {
        render: error.message || 'Failed to save preferences',
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original subscriptions
    const settings = {};
    NOTIFICATION_TYPES.forEach(type => {
      const isEnabled = type.backendTypes.some(
        backendType => subscriptions[backendType]?.includes('email')
      );
      settings[type.id] = isEnabled;
    });
    setLocalSettings(settings);
    setIsEditing(false);
  };

  if (loading && Object.keys(localSettings).length === 0) {
    return (
      <div className="px-6 py-6">
        <div className="animate-pulse">
          <div className="h-16 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-6">
      {/* Header Card */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-5 bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 shadow-sm hover:shadow"
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-11 h-11 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm">
            <Mail className="h-5 w-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-base font-semibold text-gray-900">Email Notifications</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {getEnabledCount()} of {NOTIFICATION_TYPES.length} enabled
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
            getEnabledCount() > 0 
              ? 'bg-emerald-50 text-emerald-700' 
              : 'bg-gray-100 text-gray-500'
          }`}>
            {getEnabledCount() > 0 ? 'Active' : 'Inactive'}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded Settings */}
      {isExpanded && (
        <div className="mt-4 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          {/* Notification Types List */}
          <div className="divide-y divide-gray-100">
            {NOTIFICATION_TYPES.map((type, index) => (
              <div
                key={type.id}
                className={`flex items-center justify-between p-5 ${
                  index === 0 ? '' : ''
                } hover:bg-gray-50/50 transition-colors`}
              >
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                    localSettings[type.id] 
                      ? 'bg-blue-50' 
                      : 'bg-gray-50'
                  }`}>
                    <Bell className={`h-4.5 w-4.5 ${
                      localSettings[type.id] 
                        ? 'text-blue-600' 
                        : 'text-gray-400'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900">{type.name}</h4>
                    <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{type.description}</p>
                  </div>
                </div>
                
                {/* Toggle Switch */}
                <button
                  onClick={() => handleToggle(type.id)}
                  disabled={!isEditing}
                  aria-pressed={localSettings[type.id]}
                  aria-label={`Toggle ${type.name}`}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    localSettings[type.id] 
                      ? 'bg-blue-600' 
                      : 'bg-gray-200'
                  } ${
                    !isEditing 
                      ? 'opacity-60 cursor-not-allowed' 
                      : 'cursor-pointer'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                      localSettings[type.id] ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>

          {/* Footer Actions */}
          <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Edit Preferences
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
